import { Button, ModalPanel, Title, IconDown } from "dash-styled"
import React, { useCallback, useState } from "react"
import { useBoolean } from "../../../../../../../../../Hooks/useBoolean"
import { useInput } from "../../../../../../../../../Hooks/useInput"
import { StructuredSequence } from "../../../../../../../../../Hooks/useStructuredTrainingQuestionTutorial"
import {
    APIJobModel,
    APIJobQueryModel
} from "../../../../../../../../../Models/API/JobModel"
import { FilterAndSortExpression } from "../../../../../../../../../Models/Client/FilterAndSortExpression"
import { JobContentPool, JobContentPoolParams } from "./JobContentPool"
import { Explanation } from "./Explanation"

interface TutorialExampleModalProps {
    orgUsername: string
    question?: StructuredSequence
    appID: string
    qualifierID: string
    onClose: () => void
    onAddQuestion: (question: {
        jobId?: string
        hint?: string
        explanation?: string
        isInteractive?: boolean
    }) => Promise<void>
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    params: JobContentPoolParams
    isAdding?: boolean
    onEditQuestion: (
        id: string,
        hint?: string,
        explanation?: string,
        isInteractive?: boolean,
        jobId?: string
    ) => void
}

export const TutorialExampleModal = ({
    orgUsername,
    question,
    appID,
    qualifierID,
    onClose,
    params,
    filterAndSort,
    onAddQuestion,
    isAdding,
    onEditQuestion
}: TutorialExampleModalProps) => {
    const { value: hint, onChange: onHintChange } = useInput(
        question?.summary.hint ?? ""
    )
    const { value: explanation, onChange: onExplanationChange } = useInput(
        question?.summary.explanation ?? ""
    )

    const [isInteractive, , , toggleInteractive] = useBoolean(
        !!question?.isInteractive
    )

    const [isExplanationVisible, showExplanation, hideExplanation] =
        useBoolean(false)

    const [selectedJob, setSelectedJob] = useState<APIJobModel | undefined>()
    const [detailedJob, setDetailedJob] = useState<APIJobModel | undefined>()

    const toggleSelection = useCallback(
        (model: APIJobModel) =>
            setSelectedJob(selection =>
                selection?.id === model.id ? undefined : model
            ),
        []
    )

    const clearSelection = useCallback(() => setSelectedJob(undefined), [])
    const jobToAdd = detailedJob ?? selectedJob

    const handleSave = useCallback(async () => {
        if (jobToAdd) {
            if (question?.id !== undefined) {
                await onEditQuestion(
                    question.id,
                    hint,
                    explanation,
                    isInteractive,
                    jobToAdd.id
                )
            } else {
                await onAddQuestion({
                    jobId: jobToAdd.id,
                    hint,
                    explanation,
                    isInteractive
                })
            }
            onClose()
        }
    }, [
        explanation,
        hint,
        isInteractive,
        jobToAdd,
        onAddQuestion,
        onClose,
        onEditQuestion,
        question
    ])

    const clearDetailedJob = useCallback(() => setDetailedJob(undefined), [])

    const actionText = question?.id !== undefined ? "Update" : "Add"
    const actionPerformingText =
        question?.id !== undefined ? "Updating..." : "Adding..."
    const buttonText = !jobToAdd
        ? "Next"
        : isAdding
        ? actionPerformingText
        : actionText

    const disableUpdate =
        jobToAdd?.id === question?.summary.jobId &&
        question?.isInteractive === isInteractive &&
        question?.summary.hint === hint &&
        question?.summary.explanation === explanation
    return (
        <ModalPanel
            testId="tutorial-example-modal"
            onClose={onClose}
            className="flex flex-col max-w-full w-auto ml-20% max-h-full relative h-full text-sm md:flex-1 rounded-r-none justify-start"
            disableGutters={true}
        >
            <div
                className={`flex items-center w-full ${
                    detailedJob ? "cursor-pointer" : ""
                }`}
                onClick={clearDetailedJob}
            >
                {detailedJob && (
                    <IconDown className="transform rotate-90 mr-1 text-smoke-800" />
                )}

                <Title variant="heading2" weight="semibold" className="m-0">
                    Add tutorial examples
                </Title>
            </div>
            <div className="flex flex-1 space-x-4 overflow-hidden max-w-full w-full">
                <div
                    className={`flex flex-col overflow-hidden pb-4 box-border transition-all duration-200 ${
                        !jobToAdd ? "w-full" : "w-3/4 pr-2"
                    }`}
                    onTransitionEnd={
                        jobToAdd ? showExplanation : hideExplanation
                    }
                >
                    <JobContentPool
                        orgUsername={orgUsername}
                        appID={appID}
                        qualifierID={qualifierID}
                        params={params}
                        filterAndSort={filterAndSort}
                        detailedJob={detailedJob}
                        selectedJob={selectedJob}
                        clearSelection={clearSelection}
                        toggleSelection={toggleSelection}
                        setDetailedJob={setDetailedJob}
                        initialSelectJobId={question?.summary?.jobId}
                    />
                </div>
                {isExplanationVisible && jobToAdd && (
                    <div className="flex flex-col flex-1">
                        <Explanation
                            hint={hint}
                            onHintChange={onHintChange}
                            explanation={explanation}
                            onExplanationChange={onExplanationChange}
                            isInteractive={isInteractive}
                            toggleInteractive={toggleInteractive}
                        />
                    </div>
                )}
            </div>
            <div className="flex justify-center pt-4">
                <Button
                    onClick={handleSave}
                    testId="tutorial-example-add-button"
                    className="w-max-content"
                    disabled={
                        (!selectedJob && !detailedJob) ||
                        isAdding ||
                        disableUpdate
                    }
                >
                    {buttonText}
                </Button>
            </div>
        </ModalPanel>
    )
}
